// ウィジェットの登録と mount。
//
// ★ウィジェットの実体は動的 import で読む。
//   storefront は全店の全ページ（テーマ共通部）で読まれるので、入口は小さく保つ。
//   そのページに実際に置かれているウィジェットのチャンクだけを取りに行く。

import { getSiteInfoId } from './site-id'

/** ウィジェット1個の実装。要素と、その要素に書かれた属性を受け取る。 */
export type Widget = (el: HTMLElement, ctx: WidgetContext) => void

export interface WidgetContext {
  /**
   * oppsite の SiteInfoId。data-site があればその値、無ければホスト名から引いたもの。
   * 店舗を跨いで同じJSを配るので、どちらの経路でも必ず決まっている必要がある。
   */
  siteId: number
}

/**
 * 名前 → チャンクの読み込み関数。
 * ここに書いた import() が esbuild の分割単位になる（w/chunk-*.mjs として別ファイルに出る）。
 * 新しいウィジェットはここに1行足す。
 */
const LOADERS: Record<string, () => Promise<{ default: Widget }>> = {
  'order-status': () => import('../widgets/order-status'),
  'product-review': () => import('../widgets/product-review'),
  'receipt-issue': () => import('../widgets/receipt-issue'),
  'est-receipt-issue': () => import('../widgets/receipt-issue/est'),
  'stock-shortage': () => import('../widgets/stock-shortage'),
}

/** 既に mount した要素を二重に処理しないための印。 */
const MOUNTED = 'wuWidgetMounted'

export function mountAll(root: ParentNode): void {
  root.querySelectorAll<HTMLElement>('[data-wu-widget]').forEach((el) => {
    if (el.dataset[MOUNTED] === '1') return

    const name = el.dataset.wuWidget
    if (!name) return

    const load = LOADERS[name]
    if (!load) {
      // 店側の綴り間違いは黙って死ぬと原因が分からない。コンソールにだけ出す。
      console.warn(`[wu] 未登録のウィジェットです: "${name}"`)
      return
    }

    const siteId = resolveSiteId(el.dataset.site)
    if (siteId === null) {
      console.warn(`[wu] 店を特定できません: data-site="${el.dataset.site}" hostname="${location.hostname}" (widget=${name})`)
      return
    }

    el.dataset[MOUNTED] = '1'
    load()
      .then((m) => m.default(el, { siteId }))
      .catch((e) => {
        // 1つのウィジェットの失敗で、同じページの他のウィジェットを巻き添えにしない。
        console.error(`[wu] ウィジェットの初期化に失敗しました: "${name}"`, e)
      })
  })
}

/**
 * data-site があればそれを使い、無ければホスト名から引く。
 *
 * ★属性が「書かれているのに読めない」ときはホスト名に逃がさない。
 *   綴り間違い（data-site="1 " や data-site="one"）を黙って正しい店として動かすと、
 *   間違いに気づく機会が無くなる。書いていないのなら、そもそもホスト名で引くのが設計どおり。
 */
function resolveSiteId(raw: string | undefined): number | null {
  if (raw === undefined || raw.trim() === '') return getSiteInfoId()

  const n = Number(raw)
  return Number.isInteger(n) && n > 0 ? n : null
}
